import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeSafeRequest } from '../../../common/makeSafeRequest';
import Preloader from '../../../components/common/Preloader';
import { AppInfoContext } from '../../../components/contexts/AppInfoContext';
import SystemPage from '../../../components/system-page/SystemPage';
import { ListForcePackageData, Optional, SystemSettingsObjectType } from '../../../types/common';
import { EmitterData, FiltersData, InputData } from '../../../types/systems';
import { defaultSettings, emittersSettingsTemplate } from './defaultSettings';

const Emitters: React.FC = () => {
	const [filters, setFilters] = useState<FiltersData>();
	const [inputData, setInputData] = useState<InputData>();
	const { currentPid } = useContext(AppInfoContext);

	const initialize = useCallback(
		async (pid: number, fp: number | string, side: number | string) => {
			const filters = await downloadFilters(pid, fp, side);
			const inpData = await downloadInputData();

			if (!filters || !inpData) {
				return false;
			}

			setFilters(filters);
			setInputData(inpData);
			return true;
		},
		[]
	);

	const downloadFilters = async (pid: number, fp: number | string, side: number | string) => {
		return await makeSafeRequest<FiltersData>('emitters/', 'GET', {
			params: { pid: pid, fp: fp, side: side },
		});
	};

	const downloadSystemSettings = async (systemId: number) => {
		return await makeSafeRequest<EmitterData>(`emitters/${systemId}/`, 'GET');
	};

	const downloadInputData = async () => {
		return await makeSafeRequest<InputData>('emitters/input-data/', 'GET');
	};

	const addEmitter = async (settings: Optional<EmitterData, 'id'>) => {
		return await makeSafeRequest<EmitterData>('emitters/', 'POST', {
			payload: settings,
			onSuccessMessage: 'Emitter added successfully!',
		});
	};

	const deleteEmitter = async (settingsId: number) => {
		return await makeSafeRequest<null>(`emitters/${settingsId}/`, 'DELETE', {
			onSuccessMessage: 'Emitter deleted successfully!',
		});
	};

	const saveEmitter = async (settings: Optional<EmitterData, 'id'>) => {
		return await makeSafeRequest<EmitterData>(`emitters/${settings.id}/`, 'PUT', {
			payload: settings,
			onSuccessMessage: 'Emitter saved successfully!',
		});
	};

	const duplicateEmitter = async (settingsId: number) => {
		return await makeSafeRequest<EmitterData>(`emitters/${settingsId}/duplicate/`, 'POST', {
			onSuccessMessage: 'Emitter duplicated successfully!',
		});
	};

	useEffect(() => {
		if (emittersSettingsTemplate && inputData && inputData.protocols) {
			(
				emittersSettingsTemplate.sections['Marks'].protocol_ids as SystemSettingsObjectType
			).data = inputData.protocols.map((protocol) => {
				return {
					value: protocol.id,
					label: protocol.protocol_json.name,
				};
			});
		}
	}, [inputData]);

	useEffect(() => {
		initialize(currentPid, '', '');
	}, [initialize, currentPid]);

	return filters && inputData ? (
		<SystemPage
			name="emitter"
			forcePackageData={filters.fps as ListForcePackageData[]}
			systemsData={filters.emitters as EmitterData[]}
			inputData={inputData}
			initializeFilters={initialize}
			downloadSystemSettings={downloadSystemSettings}
			systemSettingsTemplate={emittersSettingsTemplate}
			defaultSettings={defaultSettings}
			addSystem={addEmitter}
			saveSystem={saveEmitter}
			deleteSystem={deleteEmitter}
			duplicateSystem={duplicateEmitter}
		></SystemPage>
	) : (
		<Preloader></Preloader>
	);
};

export default Emitters;
